import React, { useState, useContext, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../App';
import axios from 'axios';
import { 
  ArrowLeft, 
  Clock, 
  Target, 
  CheckCircle, 
  XCircle, 
  Trophy, 
  RefreshCw,
  Star,
  Globe,
  TrendingUp,
  BookOpen,
  Volume2,
  VolumeX,
  Repeat,
  Lightbulb,
  Brain
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const YorubaLanguageGame = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [gameState, setGameState] = useState('menu'); // menu, learning, spaced_review, testing, completed
  const [sessionId, setSessionId] = useState(null);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [words, setWords] = useState([]);
  const [learnedWords, setLearnedWords] = useState([]);
  const [reviewWords, setReviewWords] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [score, setScore] = useState({ correct: 0, total: 0 });
  const [timeLeft, setTimeLeft] = useState(30);
  const [gameResults, setGameResults] = useState(null);
  const [difficulty, setDifficulty] = useState('beginner');
  const [loading, setLoading] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [lastAnswerCorrect, setLastAnswerCorrect] = useState(null);
  const [learningMode, setLearningMode] = useState('vocabulary'); // vocabulary, phrases, grammar
  const [showTranslation, setShowTranslation] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [spacedReviewDue, setSpacedReviewDue] = useState([]);

  const difficulties = [
    {
      value: 'beginner',
      label: 'Principiante',
      description: 'Vocabulario básico y saludos fundamentales',
      icon: '🌱',
      color: 'from-green-500 to-emerald-600',
      wordCount: 50
    },
    {
      value: 'intermediate', 
      label: 'Intermedio',
      description: 'Frases comunes y expresiones religiosas',
      icon: '🌿',
      color: 'from-blue-500 to-indigo-600',
      wordCount: 150
    },
    {
      value: 'advanced',
      label: 'Avanzado',
      description: 'Oraciones complejas y terminología ritual',
      icon: '🗣️',
      color: 'from-purple-500 to-violet-600',
      wordCount: 300
    },
    {
      value: 'expert',
      label: 'Experto',
      description: 'Dominio completo del Yoruba sagrado',
      icon: '👑',
      color: 'from-amber-500 to-orange-600',
      wordCount: 500
    }
  ];

  // Sample Yoruba vocabulary with spaced repetition intervals
  const sampleVocabulary = [
    {
      yoruba: "Aboru",
      spanish: "Bendición (saludo)",
      english: "Blessing (greeting)",
      pronunciation: "ah-bo-RU",
      category: "saludos",
      difficulty: "beginner",
      example: "Aboru Aboye Aboshishe",
      audio_url: "yoruba_aboru.mp3",
      repetition_level: 0,
      next_review: new Date(),
      interval_days: 1
    },
    {
      yoruba: "Aboye",
      spanish: "Nos encontremos",
      english: "May we meet",
      pronunciation: "ah-bo-YEH",
      category: "saludos",
      difficulty: "beginner",
      example: "Aboru Aboye Aboshishe",
      audio_url: "yoruba_aboye.mp3",
      repetition_level: 0,
      next_review: new Date(),
      interval_days: 1
    },
    {
      yoruba: "Aboshishe",
      spanish: "Que podamos comunicarnos",
      english: "May we communicate",
      pronunciation: "ah-bo-shi-SHEH",
      category: "saludos",
      difficulty: "beginner",
      example: "Aboru Aboye Aboshishe",
      audio_url: "yoruba_aboshishe.mp3",
      repetition_level: 0,
      next_review: new Date(),
      interval_days: 1
    },
    {
      yoruba: "Eleggua",
      spanish: "Orisha guardián de los caminos",
      english: "Guardian Orisha of the paths",
      pronunciation: "eh-leh-GUA",
      category: "orishas",
      difficulty: "beginner",
      example: "Eleggua laroye",
      audio_url: "yoruba_eleggua.mp3",
      repetition_level: 0,
      next_review: new Date(),
      interval_days: 1
    },
    {
      yoruba: "Ashé",
      spanish: "Que así sea / Fuerza vital",
      english: "So be it / Life force",
      pronunciation: "ah-SHEH",
      category: "expresiones",
      difficulty: "beginner",
      example: "Ashé to gbogbo",
      audio_url: "yoruba_ashe.mp3",
      repetition_level: 0,
      next_review: new Date(),
      interval_days: 1
    },
    {
      yoruba: "Moyuba",
      spanish: "Yo saludo/honro",
      english: "I greet/honor",
      pronunciation: "mo-yu-BA",
      category: "rituales",
      difficulty: "intermediate",
      example: "Moyuba egun, moyuba orisha",
      audio_url: "yoruba_moyuba.mp3",
      repetition_level: 0,
      next_review: new Date(),
      interval_days: 1
    },
    {
      yoruba: "Egun",
      spanish: "Ancestros / Espíritus",
      english: "Ancestors / Spirits",
      pronunciation: "eh-GUN",
      category: "espiritual",
      difficulty: "intermediate",
      example: "Egun wa pelu mi",
      audio_url: "yoruba_egun.mp3",
      repetition_level: 0,
      next_review: new Date(),
      interval_days: 1
    },
    {
      yoruba: "Ori",
      spanish: "Cabeza / Destino personal",
      english: "Head / Personal destiny",
      pronunciation: "oh-REE",
      category: "conceptos",
      difficulty: "intermediate",
      example: "Ori mi, gba mi",
      audio_url: "yoruba_ori.mp3",
      repetition_level: 0,
      next_review: new Date(),
      interval_days: 1
    },
    {
      yoruba: "Ifá",
      spanish: "Sistema de adivinación Yoruba",
      english: "Yoruba divination system",
      pronunciation: "ee-FAH",
      category: "adivinacion",
      difficulty: "advanced",
      example: "Ifá ni gbogbo",
      audio_url: "yoruba_ifa.mp3",
      repetition_level: 0,
      next_review: new Date(),
      interval_days: 1
    },
    {
      yoruba: "Babalawo",
      spanish: "Sacerdote de Ifá",
      english: "Priest of Ifá",
      pronunciation: "ba-ba-la-WO",
      category: "jerarquia",
      difficulty: "advanced",
      example: "Babalawo ko mi ni Ifá",
      audio_url: "yoruba_babalawo.mp3",
      repetition_level: 0,
      next_review: new Date(),
      interval_days: 1
    }
  ];

  // Spaced Repetition intervals (SuperMemo algorithm simplified)
  const calculateNextInterval = (repetitionLevel, wasCorrect) => {
    if (!wasCorrect) {
      return 1; // Reset to 1 day if incorrect
    }
    
    const intervals = [1, 3, 7, 14, 30, 90, 180, 365];
    return intervals[Math.min(repetitionLevel, intervals.length - 1)];
  };

  // Timer effect
  useEffect(() => {
    if (gameState === 'testing' && timeLeft > 0 && !selectedAnswer) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 && !selectedAnswer) {
      handleAnswerSubmit(''); 
    }
  }, [gameState, timeLeft, selectedAnswer]);

  const startLearning = async (selectedDifficulty) => {
    setLoading(true);
    try {
      // Filter vocabulary by difficulty
      const filteredWords = sampleVocabulary.filter(word => 
        word.difficulty === selectedDifficulty || selectedDifficulty === 'expert'
      );
      
      setWords(filteredWords);
      setDifficulty(selectedDifficulty);
      setGameState('learning');
      setCurrentWordIndex(0);
      setLearnedWords([]);
      setLearningMode('vocabulary');
      
      // Check for spaced review words
      const dueForReview = filteredWords.filter(word => 
        new Date() >= new Date(word.next_review)
      );
      setSpacedReviewDue(dueForReview);
      
      toast.success('¡Comienza tu aprendizaje de Yoruba!');
    } catch (error) {
      console.error('Error starting game:', error);
      toast.error('Error al iniciar el aprendizaje');
    } finally {
      setLoading(false);
    }
  };

  const playAudio = (audioUrl) => {
    if (audioEnabled && audioUrl) {
      // In a real implementation, this would play the audio file
      toast.success('🔊 Reproduciendo pronunciación...');
    }
  };

  const markWordAsLearned = (word, wasCorrect = true) => {
    if (!learnedWords.find(w => w.yoruba === word.yoruba)) {
      // Update spaced repetition data
      const updatedWord = {
        ...word,
        repetition_level: wasCorrect ? word.repetition_level + 1 : 0,
        last_reviewed: new Date(),
        interval_days: calculateNextInterval(word.repetition_level, wasCorrect)
      };
      
      const nextReviewDate = new Date();
      nextReviewDate.setDate(nextReviewDate.getDate() + updatedWord.interval_days);
      updatedWord.next_review = nextReviewDate;
      
      setLearnedWords(prev => [...prev, updatedWord]);
      
      if (wasCorrect) {
        toast.success(`${word.yoruba} agregado a palabras aprendidas`);
      } else {
        toast.info(`${word.yoruba} necesita más práctica`);
      }
    }
  };

  const nextWord = () => {
    if (currentWordIndex < words.length - 1) {
      setCurrentWordIndex(prev => prev + 1);
      setShowTranslation(false);
    } else {
      toast.info('Has completado todo el vocabulario disponible');
    }
  };

  const previousWord = () => {
    if (currentWordIndex > 0) {
      setCurrentWordIndex(prev => prev - 1);
      setShowTranslation(false);
    }
  };

  const startSpacedReview = () => {
    if (spacedReviewDue.length === 0) {
      toast.info('No hay palabras programadas para repaso hoy');
      return;
    }
    
    setReviewWords(spacedReviewDue);
    setGameState('spaced_review');
    setCurrentWordIndex(0);
    toast.success(`Comenzando repaso de ${spacedReviewDue.length} palabras`);
  };

  const generateTest = () => {
    if (learnedWords.length < 3) {
      toast.error('Aprende al menos 3 palabras antes de la prueba');
      return;
    }
    
    const questions = generateYorubaQuestions(learnedWords);
    setWords(questions);
    setGameState('testing');
    setScore({ correct: 0, total: 0 });
    setCurrentWordIndex(0);
    setTimeLeft(30);
  };

  const generateYorubaQuestions = (learnedWords) => {
    const questions = [];
    const shuffled = [...learnedWords].sort(() => Math.random() - 0.5);
    
    for (let i = 0; i < Math.min(10, learnedWords.length); i++) {
      const correctWord = shuffled[i];
      const wrongWords = learnedWords.filter(w => w.yoruba !== correctWord.yoruba);
      const wrongAnswers = wrongWords.sort(() => Math.random() - 0.5).slice(0, 3);
      
      // Vary question types
      const questionTypes = ['yoruba_to_spanish', 'spanish_to_yoruba', 'pronunciation', 'usage'];
      const questionType = questionTypes[Math.floor(Math.random() * questionTypes.length)];
      
      let question;
      switch (questionType) {
        case 'yoruba_to_spanish':
          question = {
            id: `q_${i}`,
            question: `¿Qué significa "${correctWord.yoruba}"?`,
            type: 'yoruba_to_spanish',
            options: [correctWord.spanish, ...wrongAnswers.map(w => w.spanish)].sort(() => Math.random() - 0.5),
            correct_answer: correctWord.spanish,
            word_data: correctWord
          };
          break;
        case 'spanish_to_yoruba':
          question = {
            id: `q_${i}`,
            question: `¿Cómo se dice "${correctWord.spanish}" en Yoruba?`,
            type: 'spanish_to_yoruba',
            options: [correctWord.yoruba, ...wrongAnswers.map(w => w.yoruba)].sort(() => Math.random() - 0.5),
            correct_answer: correctWord.yoruba,
            word_data: correctWord
          };
          break;
        case 'pronunciation':
          question = {
            id: `q_${i}`,
            question: `¿Cuál es la pronunciación correcta de "${correctWord.yoruba}"?`,
            type: 'pronunciation',
            options: [correctWord.pronunciation, ...wrongAnswers.map(w => w.pronunciation)].sort(() => Math.random() - 0.5),
            correct_answer: correctWord.pronunciation,
            word_data: correctWord
          };
          break;
        default:
          question = {
            id: `q_${i}`,
            question: `¿En qué contexto usarías "${correctWord.yoruba}"?`,
            type: 'usage',
            options: [correctWord.example, ...wrongAnswers.map(w => w.example)].sort(() => Math.random() - 0.5),
            correct_answer: correctWord.example,
            word_data: correctWord
          };
      }
      
      questions.push(question);
    }
    
    return questions;
  };

  const handleAnswerSelect = (answer) => {
    if (!selectedAnswer && timeLeft > 0) {
      setSelectedAnswer(answer);
      setTimeout(() => handleAnswerSubmit(answer), 500);
    }
  };

  const handleAnswerSubmit = async (answer) => {
    const question = words[currentWordIndex];
    if (!question) return;
    
    const isCorrect = answer === question.correct_answer;
    setLastAnswerCorrect(isCorrect);
    setShowResult(true);
    
    // Update spaced repetition for this word
    markWordAsLearned(question.word_data, isCorrect);
    
    if (isCorrect) {
      setScore(prev => ({ ...prev, correct: prev.correct + 1 }));
    }
    setScore(prev => ({ ...prev, total: prev.total + 1 }));
    
    // Show result for 2 seconds then move to next question
    setTimeout(() => {
      if (score.total + 1 < words.length) {
        setCurrentWordIndex(prev => prev + 1);
        setSelectedAnswer('');
        setTimeLeft(30);
        setShowResult(false);
        setLastAnswerCorrect(null);
      } else {
        completeGame();
      }
    }, 2000);
  };

  const completeGame = async () => {
    const percentage = Math.round((score.correct / words.length) * 100);
    
    setGameResults({
      score: percentage,
      correct_answers: score.correct,
      total_questions: words.length,
      percentage: percentage,
      words_learned: learnedWords.length
    });
    setGameState('completed');
    
    if (percentage >= 80) {
      toast.success('¡Excelente dominio del Yoruba!');
    } else if (percentage >= 60) {
      toast.success('¡Buen progreso en el aprendizaje!');
    } else {
      toast.success('¡Sigue practicando con repetición espaciada!');
    }
  };

  const resetGame = () => {
    setGameState('menu');
    setSessionId(null);
    setWords([]);
    setLearnedWords([]);
    setReviewWords([]);
    setCurrentWordIndex(0);
    setScore({ correct: 0, total: 0 });
    setTimeLeft(30);
    setSelectedAnswer('');
    setGameResults(null);
    setShowResult(false);
    setLastAnswerCorrect(null);
    setShowTranslation(false);
    setSpacedReviewDue([]);
  };

  const currentWord = words[currentWordIndex];

  // Menu Screen
  if (gameState === 'menu') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50">
        <div className="container mx-auto px-6 py-8">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <Link 
              to="/dashboard"
              className="p-2 bg-white rounded-xl shadow-lg text-emerald-600 hover:text-emerald-800 transition-colors"
            >
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <div>
              <h1 className="font-cinzel text-3xl font-bold text-emerald-800">Idioma Yoruba</h1>
              <p className="text-emerald-600">Aprende el idioma sagrado con repetición espaciada y técnicas de memoria</p>
            </div>
          </div>

          {/* Difficulty Selection */}
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="font-cinzel text-2xl font-bold text-emerald-800 mb-4">Selecciona tu Nivel de Yoruba</h2>
              <p className="text-emerald-700 max-w-2xl mx-auto">
                Domina el idioma sagrado Yoruba con vocabulario, pronunciación 
                y frases esenciales para la práctica religiosa.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              {difficulties.map((diff, index) => (
                <div 
                  key={index} 
                  className={`game-card bg-gradient-to-br ${diff.color} p-8 rounded-2xl text-white cursor-pointer relative overflow-hidden`}
                  onClick={() => startLearning(diff.value)}
                >
                  <div className="text-6xl mb-4 text-center">{diff.icon}</div>
                  <h3 className="font-cinzel text-2xl font-bold mb-3 text-center">{diff.label}</h3>
                  <p className="text-white text-opacity-90 text-center leading-relaxed mb-4">
                    {diff.description}
                  </p>
                  <div className="text-center text-white text-opacity-80 text-sm mb-6">
                    ~{diff.wordCount} palabras • Sistema SRS
                  </div>
                  
                  <div className="text-center">
                    <button 
                      className="bg-white text-emerald-800 px-6 py-3 rounded-xl font-semibold hover:scale-105 transition-all duration-300 flex items-center gap-2 mx-auto"
                      disabled={loading}
                    >
                      {loading ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-emerald-800"></div>
                      ) : (
                        <>
                          <Globe className="w-5 h-5" />
                          Comenzar Aprendizaje
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Learning Screen - Vocabulary with Spaced Repetition
  if (gameState === 'learning') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-900 via-teal-800 to-green-700">
        <div className="container mx-auto px-6 py-8">
          {/* Learning Header */}
          <div className="flex items-center justify-between mb-8 text-white">
            <div className="flex items-center gap-4">
              <button 
                onClick={resetGame}
                className="p-2 bg-white bg-opacity-20 rounded-xl hover:bg-opacity-30 transition-all"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div>
                <h1 className="font-cinzel text-2xl font-bold">Aprendizaje de Yoruba</h1>
                <p className="text-emerald-200 capitalize">{difficulty} • Repetición Espaciada</p>
              </div>
            </div>
            
            <div className="flex items-center gap-6">
              <div className="text-center">
                <div className="text-2xl font-bold">{learnedWords.length}</div>
                <div className="text-emerald-200 text-sm">Aprendidas</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold">{spacedReviewDue.length}</div>
                <div className="text-emerald-200 text-sm">Repaso Hoy</div>
              </div>
              <button
                onClick={() => setAudioEnabled(!audioEnabled)}
                className="p-2 bg-white bg-opacity-20 rounded-xl hover:bg-opacity-30 transition-all"
              >
                {audioEnabled ? <Volume2 className="w-6 h-6" /> : <VolumeX className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex justify-between text-emerald-200 text-sm mb-2">
              <span>Palabra {currentWordIndex + 1} de {words.length}</span>
              <span>{Math.round(((currentWordIndex + 1) / words.length) * 100)}% Completado</span>
            </div>
            <div className="progress-bar bg-emerald-800">
              <div 
                className="progress-fill bg-gradient-to-r from-yellow-400 to-green-400" 
                style={{ width: `${((currentWordIndex + 1) / words.length) * 100}%` }}
              ></div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="flex justify-center gap-4 mb-8">
            {spacedReviewDue.length > 0 && (
              <button
                onClick={startSpacedReview}
                className="bg-yellow-500 text-white px-6 py-3 rounded-xl font-semibold hover:bg-yellow-600 transition-colors flex items-center gap-2"
              >
                <Repeat className="w-5 h-5" />
                Repaso Programado ({spacedReviewDue.length})
              </button>
            )}
            
            {learnedWords.length >= 3 && (
              <button
                onClick={generateTest}
                className="bg-purple-500 text-white px-6 py-3 rounded-xl font-semibold hover:bg-purple-600 transition-colors flex items-center gap-2"
              >
                <Brain className="w-5 h-5" />
                Prueba de Memoria
              </button>
            )}
          </div>

          {/* Vocabulary Card */}
          {currentWord && (
            <div className="max-w-4xl mx-auto">
              <div className="bg-white rounded-2xl p-8 shadow-2xl mb-6">
                <div className="text-center mb-8">
                  <div className="flex justify-center items-center gap-4 mb-6">
                    <h2 className="font-cinzel text-5xl font-bold text-emerald-800">
                      {currentWord.yoruba}
                    </h2>
                    <button
                      onClick={() => playAudio(currentWord.audio_url)}
                      className="p-3 bg-emerald-100 rounded-full text-emerald-600 hover:bg-emerald-200 transition-colors"
                    >
                      <Volume2 className="w-6 h-6" />
                    </button>
                  </div>
                  
                  <div className="text-2xl text-emerald-600 mb-4 font-mono">
                    [{currentWord.pronunciation}]
                  </div>
                  
                  <div className="flex justify-center gap-3 mb-6">
                    <span className="bg-emerald-100 text-emerald-800 px-4 py-2 rounded-full text-sm font-semibold">
                      {currentWord.category}
                    </span>
                    <span className="bg-teal-100 text-teal-800 px-4 py-2 rounded-full text-sm font-semibold">
                      {currentWord.difficulty}
                    </span>
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-8 mb-8">
                  <div>
                    <h3 className="font-bold text-lg text-emerald-800 mb-4 flex items-center gap-2">
                      <Lightbulb className="w-5 h-5" />
                      Significados
                    </h3>
                    <div className="space-y-3">
                      <div className="p-4 bg-emerald-50 rounded-xl">
                        <div className="font-semibold text-emerald-800 mb-1">Español:</div>
                        <div className={showTranslation ? 'text-emerald-700' : 'text-gray-400 cursor-pointer'} 
                             onClick={() => setShowTranslation(true)}>
                          {showTranslation ? currentWord.spanish : 'Haz clic para revelar'}
                        </div>
                      </div>
                      <div className="p-4 bg-teal-50 rounded-xl">
                        <div className="font-semibold text-teal-800 mb-1">English:</div>
                        <div className={showTranslation ? 'text-teal-700' : 'text-gray-400 cursor-pointer'} 
                             onClick={() => setShowTranslation(true)}>
                          {showTranslation ? currentWord.english : 'Click to reveal'}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="font-bold text-lg text-emerald-800 mb-4">Ejemplo de Uso</h3>
                    <div className="p-4 bg-yellow-50 rounded-xl">
                      <div className="text-emerald-800 font-mono text-lg">"{currentWord.example}"</div>
                      <div className="text-sm text-emerald-600 mt-2">
                        Contexto cultural y religioso
                      </div>
                    </div>
                  </div>
                </div>

                {/* Memory Techniques */}
                <div className="mb-8 p-4 bg-blue-50 rounded-xl">
                  <h4 className="font-bold text-blue-800 mb-2 flex items-center gap-2">
                    <Brain className="w-5 h-5" />
                    Técnica de Memoria (Dual Coding)
                  </h4>
                  <p className="text-blue-700 text-sm">
                    <strong>Visual:</strong> Imagina la palabra "{currentWord.yoruba}" junto con su significado "{currentWord.spanish}".
                    <br />
                    <strong>Auditivo:</strong> Repite la pronunciación "{currentWord.pronunciation}" 3 veces en voz alta.
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap justify-center gap-4">
                  <button
                    onClick={previousWord}
                    disabled={currentWordIndex === 0}
                    className="px-6 py-3 bg-gray-500 text-white rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-600 transition-colors"
                  >
                    Anterior
                  </button>
                  
                  <button
                    onClick={() => markWordAsLearned(currentWord, false)}
                    className="px-6 py-3 bg-yellow-500 text-white rounded-xl font-semibold hover:bg-yellow-600 transition-colors flex items-center gap-2"
                  >
                    <Repeat className="w-5 h-5" />
                    Repasar Pronto
                  </button>
                  
                  <button
                    onClick={() => markWordAsLearned(currentWord, true)}
                    className="px-6 py-3 bg-green-500 text-white rounded-xl font-semibold hover:bg-green-600 transition-colors flex items-center gap-2"
                  >
                    <CheckCircle className="w-5 h-5" />
                    ¡La Sé!
                  </button>
                  
                  <button
                    onClick={nextWord}
                    disabled={currentWordIndex === words.length - 1}
                    className="px-6 py-3 bg-emerald-500 text-white rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-emerald-600 transition-colors"
                  >
                    Siguiente
                  </button>
                </div>
              </div>

              {/* Learned Words Progress */}
              {learnedWords.length > 0 && (
                <div className="bg-white bg-opacity-10 rounded-2xl p-6">
                  <h3 className="text-white font-bold text-lg mb-4 flex items-center gap-2">
                    <Trophy className="w-5 h-5" />
                    Palabras Dominadas ({learnedWords.length})
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {learnedWords.slice(-8).map((word, index) => (
                      <div key={index} className="text-center p-3 bg-white bg-opacity-20 rounded-xl">
                        <div className="text-white font-bold">{word.yoruba}</div>
                        <div className="text-emerald-200 text-sm">{word.spanish}</div>
                        <div className="text-xs text-emerald-300 mt-1">
                          Nivel {word.repetition_level}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Testing Screen - Same structure as other games but adapted for language learning
  if (gameState === 'testing') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-800 to-emerald-700">
        <div className="container mx-auto px-6 py-8">
          {/* Test Header */}
          <div className="flex items-center justify-between mb-8 text-white">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => setGameState('learning')}
                className="p-2 bg-white bg-opacity-20 rounded-xl hover:bg-opacity-30 transition-all"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div>
                <h1 className="font-cinzel text-2xl font-bold">Prueba de Yoruba</h1>
                <p className="text-purple-200">Demuestra tu conocimiento del idioma sagrado</p>
              </div>
            </div>
            
            <div className="flex items-center gap-6">
              <div className="text-center">
                <div className="text-2xl font-bold">{score.correct}/{score.total}</div>
                <div className="text-purple-200 text-sm">Correctas</div>
              </div>
              <div className="text-center">
                <div className={`text-3xl font-bold ${timeLeft <= 10 ? 'text-red-400 animate-pulse' : ''}`}>
                  {timeLeft}
                </div>
                <div className="text-purple-200 text-sm">Segundos</div>
              </div>
            </div>
          </div>

          {/* Test Question */}
          {currentWord && (
            <div className="max-w-4xl mx-auto">
              <div className="bg-white rounded-2xl p-8 shadow-2xl mb-6">
                <div className="text-center mb-8">
                  <h2 className="font-cinzel text-2xl font-bold text-purple-800 mb-4">
                    {currentWord.question}
                  </h2>
                  
                  {currentWord.type === 'yoruba_to_spanish' && (
                    <div className="flex justify-center items-center gap-3 mb-4">
                      <div className="text-4xl font-bold text-emerald-700">{currentWord.word_data.yoruba}</div>
                      <button
                        onClick={() => playAudio(currentWord.word_data.audio_url)}
                        className="p-2 bg-emerald-100 rounded-full text-emerald-600 hover:bg-emerald-200 transition-colors"
                      >
                        <Volume2 className="w-5 h-5" />
                      </button>
                    </div>
                  )}
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  {currentWord.options.map((option, index) => (
                    <button
                      key={index}
                      onClick={() => handleAnswerSelect(option)}
                      disabled={selectedAnswer || timeLeft === 0}
                      className={`p-6 rounded-xl text-left font-semibold transition-all duration-300 border-2 ${
                        selectedAnswer === option
                          ? showResult
                            ? lastAnswerCorrect && selectedAnswer === currentWord.correct_answer
                              ? 'border-green-500 bg-green-50 text-green-800'
                              : 'border-red-500 bg-red-50 text-red-800'
                            : 'border-purple-500 bg-purple-50 text-purple-800'
                          : showResult && option === currentWord.correct_answer
                          ? 'border-green-500 bg-green-50 text-green-800'
                          : 'border-gray-200 bg-gray-50 text-gray-700 hover:border-purple-300 hover:bg-purple-50 hover:text-purple-700'
                      } ${selectedAnswer || timeLeft === 0 ? 'cursor-not-allowed' : 'cursor-pointer hover:scale-[1.02]'}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center text-sm font-bold ${
                          selectedAnswer === option
                            ? showResult
                              ? lastAnswerCorrect && selectedAnswer === currentWord.correct_answer
                                ? 'border-green-500 bg-green-500 text-white'
                                : 'border-red-500 bg-red-500 text-white'
                              : 'border-purple-500 bg-purple-500 text-white'
                            : showResult && option === currentWord.correct_answer
                            ? 'border-green-500 bg-green-500 text-white'
                            : 'border-gray-400 text-gray-600'
                        }`}>
                          {String.fromCharCode(65 + index)}
                        </div>
                        <span>{option}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Result Feedback */}
              {showResult && (
                <div className={`text-center p-6 rounded-2xl ${
                  lastAnswerCorrect ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  <div className="flex items-center justify-center gap-3 mb-2">
                    {lastAnswerCorrect ? (
                      <CheckCircle className="w-8 h-8 text-green-600" />
                    ) : (
                      <XCircle className="w-8 h-8 text-red-600" />
                    )}
                    <span className="text-xl font-bold">
                      {lastAnswerCorrect ? '¡Correcto! Excelente memoria' : 'Incorrecto'}
                    </span>
                  </div>
                  {!lastAnswerCorrect && (
                    <div>
                      <p>La respuesta correcta era: <strong>{currentWord.correct_answer}</strong></p>
                      <p className="text-sm mt-2">Esta palabra necesita más práctica con repetición espaciada</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Completed Screen - Similar to other games but with language learning focus
  if (gameState === 'completed' && gameResults) {
    const getScoreMessage = (percentage) => {
      if (percentage >= 90) return { text: '¡Yoruba Competente! Dominio excepcional del idioma sagrado', icon: '🏆', color: 'text-yellow-600' };
      if (percentage >= 80) return { text: '¡Excelente! Gran progreso en Yoruba', icon: '⭐', color: 'text-purple-600' };
      if (percentage >= 60) return { text: '¡Buen avance! Continúa con la repetición espaciada', icon: '👍', color: 'text-blue-600' };
      return { text: 'Sigue practicando diariamente para mejorar retención', icon: '💪', color: 'text-emerald-600' };
    };

    const scoreMessage = getScoreMessage(gameResults.percentage);

    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center p-6">
        <div className="max-w-2xl w-full">
          <div className="bg-white rounded-2xl p-8 shadow-2xl text-center">
            <div className="mb-8">
              <div className="text-6xl mb-4">{scoreMessage.icon}</div>
              <h1 className="font-cinzel text-3xl font-bold text-emerald-800 mb-4">¡Evaluación Completada!</h1>
              <p className={`text-lg font-semibold ${scoreMessage.color}`}>{scoreMessage.text}</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
              <div className="text-center">
                <div className="bg-emerald-100 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <Trophy className="w-8 h-8 text-emerald-600" />
                </div>
                <div className="text-2xl font-bold text-emerald-800">{gameResults.percentage}%</div>
                <div className="text-sm text-gray-600">Puntuación</div>
              </div>
              
              <div className="text-center">
                <div className="bg-green-100 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <div className="text-2xl font-bold text-emerald-800">{gameResults.correct_answers}</div>
                <div className="text-sm text-gray-600">Correctas</div>
              </div>
              
              <div className="text-center">
                <div className="bg-blue-100 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <Target className="w-8 h-8 text-blue-600" />
                </div>
                <div className="text-2xl font-bold text-emerald-800">{gameResults.total_questions}</div>
                <div className="text-sm text-gray-600">Preguntas</div>
              </div>
              
              <div className="text-center">
                <div className="bg-yellow-100 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <Globe className="w-8 h-8 text-yellow-600" />
                </div>
                <div className="text-2xl font-bold text-emerald-800">{gameResults.words_learned}</div>
                <div className="text-sm text-gray-600">Aprendidas</div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={resetGame}
                className="btn-primary px-6 py-3 font-semibold rounded-xl flex items-center justify-center gap-2"
              >
                <RefreshCw className="w-5 h-5" />
                Continuar Aprendiendo
              </button>
              
              <Link
                to="/dashboard"
                className="btn-gold px-6 py-3 font-semibold rounded-xl flex items-center justify-center gap-2"
              >
                <TrendingUp className="w-5 h-5" />
                Ver Progreso
              </Link>
            </div>

            <div className="mt-8 p-4 bg-emerald-50 rounded-xl">
              <p className="text-emerald-700 text-sm">
                <strong>Próximo Paso:</strong> {gameResults.percentage >= 80 
                  ? 'Excelente uso de repetición espaciada. Tus palabras serán reprogramadas automáticamente.'
                  : 'Repite las palabras incorrectas mañana. El sistema ajustará los intervalos de repaso.'
                }
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default YorubaLanguageGame;